import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import InlineForm from '../components/InlineForm';
import ConfirmDialog from '../components/ConfirmDialog';
import Icon from '../components/Icon';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { number, relativeAge } from '../lib/format';
import { ICONS } from '../lib/icons';
import { providerNav } from '../lib/providerNav';

// Region subscriptions: which regions the tenancy is signed up to, and adding
// one.
//
// This is the page that owns the region snapshot. The list itself is a database
// read like everywhere else in the console; *Refresh* is what calls the provider's
// SDK and stores the result, so every other screen's region list is as current as
// the last refresh here. That is deliberate — regions sit behind pickers and a
// landing-page KPI, and none of those should cost an API call.
//
// There is no remove, here or anywhere below this page. OCI has no unsubscribe —
// a subscription is permanent — so the page says so rather than offering a
// control that would fail.
export default function ProviderRegions() {
  const { id } = useParams();
  const { setNav } = useSideNav();
  const { isAdmin } = useAuth();

  const providerReq = useAsync(() => api.getProvider(id), [id]);
  const provider = providerReq.data;

  // The envelope, for meta.syncedAt: a stored list looks equally current whether
  // it was written a minute or a month ago, so the page has to say which.
  const regionsReq = useAsync(() => api.getRegionsEnvelope(id), [id]);
  const regions = useMemo(() => regionsReq.data?.data || [], [regionsReq.data]);
  // The snapshot's age, or '' when it has never been written — which includes rows
  // that predate the sync (a column upgrade left them at the epoch).
  const syncedAge = relativeAge(regionsReq.data?.meta?.syncedAt);

  // The vendor's whole region list, only for the picker — and only for an admin,
  // who is the only one who can act on it.
  const catalogueReq = useAsync(
    () => (isAdmin ? api.getAvailableRegions(id) : Promise.resolve([])),
    [id, isAdmin]
  );
  const catalogue = useMemo(() => catalogueReq.data || [], [catalogueReq.data]);

  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    if (provider) setNav(providerNav(provider));
  }, [provider, setNav]);
  useEffect(() => () => setNav(null), [setNav]);

  const homeRegion = regions.find((r) => r.home);

  // What is left to add: the catalogue minus what the tenancy already has,
  // matched on the key, which is the provider's own identifier for a region and
  // the value a subscription is created with.
  const unsubscribed = useMemo(() => {
    const held = new Set(regions.map((r) => r.key).filter(Boolean));
    return catalogue.filter((r) => r.key && !held.has(r.key));
  }, [catalogue, regions]);

  // Refresh means "ask the provider and store what it says", not "re-read the
  // page": a plain reload would show the same snapshot back. Only a superadmin can
  // write it, so everyone else re-reads instead of silently doing nothing.
  const refresh = async () => {
    if (!isAdmin) {
      regionsReq.reload();
      return;
    }
    setSyncing(true);
    setSyncError(null);
    setAdded(null);
    try {
      await api.syncRegions(id);
      regionsReq.reload();
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const subscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      const region = await api.subscribeRegion(id, confirm.key);
      setAdded(region);
      setConfirm(null);
      setForm(null);
      regionsReq.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (providerReq.loading) return <Loading label="Loading provider…" />;
  if (providerReq.error) {
    return <ErrorState error={providerReq.error} onRetry={providerReq.reload} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Region subscriptions"
        subtitle={`${provider?.name || id} — the regions this tenancy can use`}
        actions={
          <>
            <button
              className="btn btn--ghost"
              onClick={refresh}
              disabled={syncing}
              title={
                isAdmin
                  ? 'Read the subscriptions from the provider and store them'
                  : 'Re-read the stored snapshot (refreshing it is superadmin only)'
              }
            >
              <Icon name={ICONS.refresh} />{' '}
              {syncing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              className="btn btn--primary"
              onClick={() => {
                setError(null);
                setAdded(null);
                setForm({ key: unsubscribed[0]?.key || '' });
              }}
              disabled={!isAdmin || unsubscribed.length === 0}
              title={
                !isAdmin
                  ? 'Superadmin role required'
                  : unsubscribed.length === 0
                  ? 'Subscribed to every region the provider offers'
                  : undefined
              }
            >
              + Subscribe region
            </button>
          </>
        }
      />

      <div className="grid grid--kpi">
        <StatCard
          label="Subscribed"
          value={number(regions.length)}
          hint={syncedAge ? `synced ${syncedAge}` : 'never synced'}
        />
        <StatCard
          label="Home region"
          value={homeRegion?.id || '—'}
          hint={homeRegion ? 'holds IAM · permanent' : 'not reported'}
        />
        <StatCard
          label="Available to add"
          value={isAdmin ? number(unsubscribed.length) : '—'}
          hint={isAdmin ? 'not yet subscribed' : 'superadmin only'}
        />
      </div>

      {syncError ? <div className="form-error">{syncError}</div> : null}

      {added ? (
        <p className="field__hint">
          Subscribed to <strong>{added.id || added.key}</strong>. A new region is
          provisioned over the next few minutes — it reads as limited until the
          provider reports it ready, so refresh again once it is.
        </p>
      ) : null}

      <Card
        className="card--quiet-title"
        title={`Subscribed regions (${number(regions.length)})`}
        action={
          <span className="field__hint">
            {syncedAge ? `stored snapshot · synced ${syncedAge}` : 'stored snapshot'}
          </span>
        }
      >
        {form ? (
          <InlineForm
            title="Subscribe to a region"
            onSubmit={(e) => {
              e.preventDefault();
              const region = unsubscribed.find((r) => r.key === form.key);
              if (!region) {
                setError('Select a region');
                return;
              }
              // Permanent, so it goes through a confirmation rather than
              // committing on submit.
              setConfirm(region);
            }}
            onCancel={() => {
              setForm(null);
              setError(null);
            }}
            submitLabel="Review subscription"
            error={error}
          >
            <div className="form-grid">
              <label className="field">
                <span>Region</span>
                <select
                  className="input"
                  value={form.key}
                  onChange={(e) => setForm({ key: e.target.value })}
                  autoFocus
                >
                  {unsubscribed.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.id} · {r.key}
                    </option>
                  ))}
                </select>
                <span className="field__hint">
                  {catalogueReq.loading
                    ? 'Reading the provider’s region list…'
                    : `${number(unsubscribed.length)} of ${number(
                        catalogue.length
                      )} regions the provider offers are not subscribed.`}
                </span>
              </label>
            </div>
            <p className="field__hint">
              Subscribing is permanent: OCI has no unsubscribe, so a region added
              here stays on the tenancy. It does not create or cost anything by
              itself — it makes the region available to create in.
            </p>
          </InlineForm>
        ) : null}

        {regionsReq.loading ? (
          <Loading label="Loading regions…" />
        ) : regionsReq.error ? (
          <ErrorState error={regionsReq.error} onRetry={regionsReq.reload} />
        ) : regions.length === 0 ? (
          <EmptyState
            // Nothing seeds this table, so empty means the snapshot has never
            // been written — not that the tenancy has no regions. Every tenancy
            // has at least its home region.
            title={syncedAge ? 'No regions' : 'Not synced yet'}
            detail={
              syncedAge
                ? 'The provider reported no region subscriptions for this tenancy.'
                : isAdmin
                ? 'Click Refresh to read the tenancy’s region subscriptions from the provider and store them.'
                : 'Ask an administrator to refresh the region subscriptions.'
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Key</th>
                  <th>Status</th>
                  <th>Synced</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="cell-primary mono">{r.id}</span>
                      {r.home ? (
                        <span className="badge badge--info" style={{ marginLeft: 8 }}>
                          Home
                        </span>
                      ) : null}
                    </td>
                    <td className="cell-type">{r.key || '—'}</td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td
                      className="cell-sub"
                      title={
                        relativeAge(r.updatedAt)
                          ? new Date(r.updatedAt).toLocaleString()
                          : ''
                      }
                    >
                      {relativeAge(r.updatedAt) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="field__hint">
          A resource can only exist in a subscribed region, and this list is what
          every region picker in the console reads. It is a stored snapshot:
          Refresh asks the provider and writes what it returns, so a subscription
          made outside this console appears here only after a refresh. The home
          region holds IAM and cannot be unsubscribed — and neither can any other:
          OCI has no unsubscribe, which is why nothing here removes one.
        </p>
      </Card>

      {confirm ? (
        <ConfirmDialog
          title="Subscribe to this region?"
          message={`Subscribe the tenancy to ${confirm.id} (${confirm.key})?`}
          detail="This cannot be undone — OCI has no unsubscribe. The region is provisioned over the next few minutes and then becomes available to create in. Subscribing alone provisions nothing and costs nothing."
          confirmLabel="Subscribe permanently"
          busy={busy}
          error={error}
          onConfirm={subscribe}
          onCancel={() => {
            setConfirm(null);
            setError(null);
          }}
        />
      ) : null}
    </div>
  );
}
